import { ActionIcon, Badge, Flex, rem } from "@mantine/core";
import { IconChevronLeft, IconChevronRight } from "@tabler/icons-react";
import { useEffect, useRef, useState } from "react";

type OrderBadgesScrollProps = {
  orderNos: string[];
  onOrderNoClick: (orderNo: string) => void;
};

export function OrderBadgesScroll({
  orderNos,
  onOrderNoClick,
}: OrderBadgesScrollProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const [translateX, setTranslateX] = useState(0);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  useEffect(() => {
    setTranslateX(0);
  }, [orderNos]);

  useEffect(() => {
    const checkButtons = () => {
      if (containerRef.current && contentRef.current) {
        const containerWidth = containerRef.current.offsetWidth;
        const contentWidth = contentRef.current.offsetWidth;

        const hasOverflow = contentWidth > containerWidth;
        const maxTranslate = contentWidth - containerWidth;

        setCanScrollLeft(translateX < -1);
        setCanScrollRight(
          hasOverflow && Math.abs(translateX) < maxTranslate - 1
        );
      }
    };

    checkButtons();

    const observer = new ResizeObserver(checkButtons);
    if (containerRef.current) observer.observe(containerRef.current);
    if (contentRef.current) observer.observe(contentRef.current);

    window.addEventListener("resize", checkButtons);
    return () => {
      observer.disconnect();
      window.removeEventListener("resize", checkButtons);
    };
  }, [orderNos, translateX]);

  const slide = (direction: "left" | "right") => {
    if (containerRef.current && contentRef.current) {
      const containerWidth = containerRef.current.offsetWidth;
      const contentWidth = contentRef.current.offsetWidth;
      const slideAmount = 200;

      let newTranslateX = translateX;
      if (direction === "left") {
        newTranslateX = Math.min(0, translateX + slideAmount);
      } else {
        newTranslateX = Math.max(
          -(contentWidth - containerWidth),
          translateX - slideAmount
        );
      }

      setTranslateX(newTranslateX);
    }
  };

  return (
    <Flex gap="xs" align="center">
      <p className="shrink-0 text-sm leading-none text-[#636363]">Orders:</p>
      {canScrollLeft && (
        <ActionIcon
          size="sm"
          variant="light"
          radius="xl"
          onClick={() => slide("left")}
        >
          <IconChevronLeft size={14} />
        </ActionIcon>
      )}
      <div
        ref={containerRef}
        style={{
          overflow: "hidden",
          flex: 1,
          position: "relative",
        }}
      >
        <div
          ref={contentRef}
          style={{
            display: "flex",
            gap: rem(8),
            transform: `translateX(${translateX}px)`,
            transition: "transform 0.3s ease",
            width: "max-content",
          }}
        >
          {orderNos.map(orderNo => (
            <Badge
              key={orderNo}
              size="sm"
              variant="light"
              style={{ cursor: "pointer", flexShrink: 0 }}
              onClick={() => onOrderNoClick(orderNo)}
            >
              #{orderNo}
            </Badge>
          ))}
        </div>
      </div>
      {canScrollRight && (
        <ActionIcon
          size="sm"
          variant="light"
          radius="xl"
          onClick={() => slide("right")}
        >
          <IconChevronRight size={14} />
        </ActionIcon>
      )}
    </Flex>
  );
}
